import MyAccountNavigation from 'components/MyAccount/Navigation';
import type { ProfileSummaryInfo } from 'components/MyAccount/ProfileSummary';
import type { CustomFieldValues, AccessValues } from 'lib/personalizationApi/entities/MyAccountDataResponse';
import { NoticeContext } from 'providers/NoticeProvider';
import { UserContext } from 'providers/UserProvider';
import { useContext, useState, useEffect, type JSX } from 'react';
import { daysUntilDate } from 'utils/dateUtils';
import useBreakpoint from 'hooks/useBreakpoint';
import { Content, StickyNavWrapper, Wrapper } from './MyAccountLayoutStyles';

interface MyAccountLayoutProps {
  children: JSX.Element;
  activeLink: string;
  forceNav?: boolean;
  hideContent?: boolean;
  newslettersCount?: number | null | undefined;
}

const MyAccountLayout = (props: MyAccountLayoutProps) => {
  const { children, activeLink, forceNav, hideContent = false, newslettersCount } = props;
  const isMobile = useBreakpoint('mdMax');
  const showNav = forceNav !== undefined ? forceNav : !isMobile;
  const {
    userState: { myAccountData, fetchError },
  } = useContext(UserContext);
  const { setNotification } = useContext(NoticeContext);
  const defaultAccountInfo = {
    accesses: [],
    email: '',
    firstName: '',
    lastName: '',
    pianoAccessName: '',
    subscriptionInfo: { daysLeft: 0, status: '' },
  };
  const [accountInfo, setAccountInfo] = useState<ProfileSummaryInfo>(defaultAccountInfo);

  useEffect(() => {
    if (myAccountData && myAccountData.customFields.success && myAccountData.accesses.success) {
      const {
        customFields: {
          value: { firstName, lastName, email },
        },
        accesses: {
          value: { accessDisplay, accesses },
        },
      } = myAccountData as { customFields: { value: CustomFieldValues }; accesses: { value: AccessValues } };
      const daysLeft = daysUntilDate(accessDisplay.details.next_bill_date);
      const profileSummaryData: ProfileSummaryInfo = {
        accesses: accesses.active.map((access) => access.name),
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
        pianoAccessName: accessDisplay.name,
        subscriptionInfo: {
          daysLeft,
          status: accessDisplay.details.status,
        },
      };
      setAccountInfo(profileSummaryData);
    } else if (fetchError) {
      setNotification?.('error', 'There was an error fetching your account data. Please try again later.');
    }
  }, [myAccountData, fetchError]);

  return (
    <Wrapper>
      {showNav && (
        <StickyNavWrapper>
          <MyAccountNavigation
            activeLink={activeLink}
            account={accountInfo}
            newslettersCount={newslettersCount}
          />
        </StickyNavWrapper>
      )}
      {!hideContent && <Content>{children}</Content>}
    </Wrapper>
  );
};

export default MyAccountLayout;
